import React, { useMemo, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Lock, X } from 'lucide-react';
import {
  TYPES_MAGASIN_ACTIFS, LOCALISATIONS, getTypeMagasin, getLocalisation,
  compatibles, computeDevis,
} from '../../utils/ouverture';
import { getVille } from '../../utils/villes';
import { getAllPositionnements, getAllSecteurs } from '../../utils/retail';
import { getDecorUrl, getVisuelUrl } from '../../utils/visuelMagasin';

/* SCD R2 — Décor réel de la localisation (image photoréaliste). */
const SceneLoc = ({ locId, actif }) => (
  <img src={getDecorUrl(locId)} alt="" loading="lazy"
    className={`w-full h-24 rounded-md object-cover transition-all duration-500 ${actif ? 'brightness-110' : 'brightness-90'}`} />
);

const Jauge = ({ label, valeur, max, testId }) => (
  <div data-testid={testId}>
    <div className="flex justify-between text-[10px] text-slate-400"><span>{label}</span><span className="font-mono text-slate-200">{typeof valeur === 'string' ? valeur : `×${valeur}`}</span></div>
    <div className="h-1.5 rounded-full bg-ink-700 overflow-hidden">
      <div className="h-full rounded-full bg-accent transition-all duration-700" style={{ width: `${Math.min(100, (parseFloat(valeur) / max) * 100)}%` }} />
    </div>
  </div>
);

/**
 * Tunnel d'ouverture d'un magasin.
 * - mode "premier" (création) : secteur imposé (étape Concept), financement
 *   forcé par emprunt (lancement de la société).
 * - mode "expansion" : étape Secteur en plus — chaque secteur peut avoir son
 *   positionnement/localisation, mais la 1re config d'un secteur FIGE ces
 *   sous-options pour tous les futurs magasins du même secteur.
 */
export default function TunnelOuverture({ mode = 'premier', secteurId, secteurConfigs = {}, treasury = 0, villeId = null, typesExclus = [], onComplete, onCancel }) {
  const expansion = mode === 'expansion';
  const minEtape = expansion ? 0 : 1;
  const [etape, setEtape] = useState(minEtape);
  const [secteur, setSecteur] = useState(secteurId || '');
  const [typeId, setTypeId] = useState('');
  const cfgFige = secteur ? secteurConfigs[secteur] || null : null;
  const [positionnement, setPositionnement] = useState(cfgFige?.positionnement || '');
  const [localisationId, setLocalisationId] = useState(cfgFige?.localisationId || '');
  const [nomMagasin, setNomMagasin] = useState('');
  const [financement, setFinancement] = useState(mode === 'premier' ? 'emprunt' : 'tresorerie');
  const [survol, setSurvol] = useState(null);
  const ville = villeId ? getVille(villeId) : null;

  const secteurs = getAllSecteurs();
  const positionnements = getAllPositionnements();
  const devis = useMemo(
    () => (typeId && positionnement && localisationId && secteur)
      ? computeDevis({ typeId, positionnement, localisationId, secteurId: secteur, villeId })
      : null,
    [typeId, positionnement, localisationId, secteur, villeId],
  );

  const choisirSecteur = (id) => {
    setSecteur(id);
    const cfg = secteurConfigs[id] || null;
    setPositionnement(cfg?.positionnement || '');
    setLocalisationId(cfg?.localisationId || '');
  };

  const peutContinuer = etape === 0 ? !!secteur : etape === 1 ? !!typeId : etape === 2 ? !!positionnement : etape === 3 ? !!localisationId : true;
  const fondsOk = devis ? (financement === 'emprunt' || treasury >= devis.coutOuverture) : false;
  const titres = expansion
    ? ['Secteur', 'Type de magasin', 'Positionnement marché', 'Localisation', 'Récapitulatif']
    : ['Type de magasin', 'Positionnement marché', 'Localisation', 'Récapitulatif'];

  const confirmer = () => onComplete({
    typeId, positionnement, localisationId, financement, villeId, secteurId: secteur,
    nom: nomMagasin.trim() || `${getTypeMagasin(typeId).nom} — ${ville?.nom || getLocalisation(localisationId).nom}`,
    devis,
  });

  return (
    <div className="space-y-4" data-testid="tunnel-ouverture">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-slate-400 flex-wrap">
          {ville && (
            <span className="px-2 py-0.5 rounded-full border border-amber-500/50 text-amber-300 font-bold" data-testid="tunnel-ville-chip">
              📍 {ville.drapeau} {ville.nom}
            </span>
          )}
          {titres.map((t, i) => {
            const num = i + minEtape;
            return (
              <React.Fragment key={t}>
                <span className={`px-2 py-0.5 rounded-full border ${etape === num ? 'border-accent text-accent font-bold' : etape > num ? 'border-emerald-500/50 text-emerald-300' : 'border-ink-600'}`}>{i + 1}. {t}</span>
                {i < titres.length - 1 && <ChevronRight className="w-3 h-3" />}
              </React.Fragment>
            );
          })}
        </div>
        {onCancel && <button onClick={onCancel} className="text-slate-500 hover:text-slate-300" data-testid="tunnel-annuler"><X className="w-4 h-4" /></button>}
      </div>

      {etape === 0 && (
        <div className="space-y-3" data-testid="tunnel-etape-secteur">
          <p className="text-xs text-slate-400">Chaque secteur a son propre concept : la première ouverture d'un secteur fige son positionnement et sa localisation pour tous les magasins suivants de ce secteur.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {secteurs.map(sec => {
              const deja = !!secteurConfigs[sec.id];
              return (
                <button key={sec.id} onClick={() => choisirSecteur(sec.id)}
                  className={`text-left rounded-lg border p-4 transition-all ${secteur === sec.id ? 'border-accent bg-accent/10 ring-1 ring-accent' : 'border-ink-600 bg-ink-900/40 hover:border-ink-500'}`}
                  data-testid={`tunnel-secteur-${sec.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{sec.emoji}</span>
                    <span className="font-bold text-foreground">{sec.nom}</span>
                    {secteur === sec.id && <Check className="w-4 h-4 text-accent ml-auto" />}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">{sec.description}</p>
                  {deja && (
                    <p className="text-[10px] text-sky-300 mt-2 flex items-center gap-1" data-testid={`secteur-config-figee-${sec.id}`}>
                      <Lock className="w-3 h-3" /> Concept figé : {getLocalisation(secteurConfigs[sec.id].localisationId)?.nom || '—'} · {secteurConfigs[sec.id].positionnement}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {etape === 1 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tunnel-etape-type">
          {TYPES_MAGASIN_ACTIFS.map(t => {
            const exclu = typesExclus.includes(t.id);
            return (
              <button key={t.id} disabled={exclu} onClick={() => !exclu && setTypeId(t.id)}
                className={`text-left rounded-lg border p-4 transition-all ${exclu ? 'border-ink-700 bg-ink-900/20 opacity-45 cursor-not-allowed' : typeId === t.id ? 'border-accent bg-accent/10 ring-1 ring-accent' : 'border-ink-600 bg-ink-900/40 hover:border-ink-500'}`}
                data-testid={`type-magasin-${t.id}`}>
                <p className="text-2xl">{t.emoji}</p>
                <p className="font-bold text-foreground mt-1">{t.nom} {typeId === t.id && <Check className="w-4 h-4 text-accent inline" />}</p>
                <p className="text-[11px] text-slate-400">{t.surface_min}–{t.surface_max} m²</p>
                {exclu
                  ? <p className="text-[10px] font-bold text-amber-300 mt-2" data-testid={`type-deja-present-${t.id}`}>Déjà présent dans cette ville</p>
                  : <>
                    <p className="text-xs text-slate-400 mt-2">{t.description}</p>
                    <p className="text-[10px] text-amber-300/80 mt-2">{t.assortiment_note}</p>
                  </>}
              </button>
            );
          })}
          <div className="rounded-lg border border-dashed border-ink-600 bg-ink-900/20 p-4 opacity-60 md:col-span-2" data-testid="type-gss-a-venir">
            <p className="font-bold text-slate-400">🏭 Grande surface spécialisée <span className="text-[10px] uppercase tracking-wider text-amber-300/80 ml-2">À venir</span></p>
            <p className="text-[11px] text-slate-500 mt-1">Ce format sera débloqué dans une prochaine version du jeu.</p>
          </div>
        </div>
      )}

      {etape === 2 && (
        <div className="space-y-3" data-testid="tunnel-etape-positionnement">
          {cfgFige && (
            <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs text-sky-200 flex items-center gap-2" data-testid="positionnement-herite">
              <Lock className="w-3.5 h-3.5" /> Positionnement figé pour ce secteur : il a été défini à la première ouverture et s'applique à tous vos magasins {getAllSecteurs().find(x => x.id === secteur)?.nom || ''}.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {positionnements.map(p => {
              const verrou = cfgFige && cfgFige.positionnement !== p.id;
              return (
                <button key={p.id} disabled={verrou} onClick={() => !verrou && setPositionnement(p.id)}
                  className={`text-left rounded-lg border p-4 transition-all ${positionnement === p.id ? 'border-accent bg-accent/10 ring-1 ring-accent' : 'border-ink-600 bg-ink-900/40 hover:border-ink-500'} ${verrou ? 'opacity-40 cursor-not-allowed' : ''}`}
                  data-testid={`tunnel-pos-${p.id}`}>
                  <p className="font-bold text-foreground">{p.nom} {positionnement === p.id && <Check className="w-4 h-4 text-accent inline" />}</p>
                  <p className="text-xs text-slate-400 mt-1">{p.description}</p>
                  <p className="text-[10px] text-amber-300/80 mt-2">Agencement ×{({ entree: 0.85, coeur: 1, premium: 1.35 })[p.id]}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {etape === 3 && (
        <div className="space-y-4" data-testid="tunnel-etape-localisation">
          {cfgFige?.localisationId && (
            <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs text-sky-200 flex items-center gap-2" data-testid="localisation-heritee">
              <Lock className="w-3.5 h-3.5" /> Emplacement figé pour ce secteur : tous vos magasins de ce secteur ouvrent en {getLocalisation(cfgFige.localisationId)?.nom}.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {LOCALISATIONS.map(l => {
              const compat = compatibles(typeId, l.id);
              const verrou = cfgFige?.localisationId && cfgFige.localisationId !== l.id;
              const choisi = localisationId === l.id;
              const gros = survol === l.id || choisi;
              return (
                <button key={l.id} disabled={!compat.ok || verrou}
                  onClick={() => compat.ok && !verrou && setLocalisationId(l.id)}
                  onMouseEnter={() => setSurvol(l.id)} onMouseLeave={() => setSurvol(null)}
                  className={`text-left rounded-lg border p-3 transition-all duration-300 ${choisi ? 'border-accent bg-accent/10 ring-1 ring-accent' : 'border-ink-600 bg-ink-900/40 hover:border-ink-500'} ${(!compat.ok || verrou) ? 'opacity-45 cursor-not-allowed' : ''} ${gros ? 'md:scale-[1.03]' : ''}`}
                  data-testid={`localisation-${l.id}`}>
                  <SceneLoc locId={l.id} actif={gros && compat.ok} />
                  <p className="font-bold text-foreground mt-2">{l.emoji} {l.nom} <span className="text-[10px] text-slate-500 font-normal">({l.sous_titre})</span> {choisi && <Check className="w-4 h-4 text-accent inline" />}</p>
                  {!compat.ok ? (
                    <p className="text-[11px] text-red-300 mt-1" data-testid={`incompatible-${l.id}`}>🚫 {compat.raison}</p>
                  ) : verrou ? (
                    <p className="text-[11px] text-sky-300/80 mt-1">🔒 Secteur figé sur {getLocalisation(cfgFige.localisationId)?.nom}</p>
                  ) : (
                    <div className={`mt-1 space-y-0.5 text-[10px] transition-all ${gros ? 'opacity-100' : 'opacity-70'}`}>
                      {l.avantages.slice(0, gros ? 3 : 2).map((a, i) => <p key={i} className="text-emerald-300">+ {a}</p>)}
                      {gros && l.inconvenients.map((a, i) => <p key={i} className="text-red-300/90">− {a}</p>)}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {devis && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-lg border border-ink-600 bg-ink-900/40 p-3 space-y-2" data-testid="jauges-comparatives">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Comparatif — {devis.type.nom} · {getLocalisation(localisationId)?.nom}</p>
                <Jauge label="Trafic estimé" valeur={(devis.loc.coef_trafic * devis.type.trafic_base_mult).toFixed(2)} max={2.2} testId="jauge-trafic" />
                <Jauge label="Loyer / m²" valeur={devis.loc.coef_loyer.toFixed(1)} max={2} testId="jauge-loyer" />
                <Jauge label="Panier moyen" valeur={devis.loc.coef_panier.toFixed(1)} max={1.5} testId="jauge-panier" />
                <Jauge label="Concurrence" valeur={devis.loc.concurrence} max={1} testId="jauge-concurrence" />
                <Jauge label="Surface" valeur={`${devis.surface} m²`} max={1} testId="jauge-surface" />
              </div>
              <div className="rounded-lg border border-ink-600 bg-ink-900/40 p-3" data-testid="apercu-magasin">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Aperçu de votre futur magasin</p>
                <div className="relative rounded-md overflow-hidden">
                  <img
                    src={getVisuelUrl({ localisationId, typeId, secteurId: secteur, positionnement })}
                    alt="Aperçu du magasin" loading="lazy"
                    className="w-full max-h-48 object-cover"
                    data-testid="apercu-visuel-img" />
                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2 rounded bg-ink-950/85 border border-accent/60 px-3 py-1 text-center">
                    <p className="text-lg leading-none">{devis.type.emoji}</p>
                    <p className="text-[9px] font-bold text-accent">{devis.type.nom} · {devis.surface} m²</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {etape === 4 && devis && (
        <div className="space-y-4" data-testid="tunnel-etape-recap">
          <img
            src={getVisuelUrl({ localisationId, typeId, secteurId: secteur, positionnement })}
            alt="Votre futur magasin" loading="lazy"
            className="w-full max-h-56 object-cover rounded-lg border border-ink-600"
            data-testid="recap-visuel-img" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              ['Coût d\'ouverture', `${devis.coutOuverture.toLocaleString('fr-FR')} €`, 'recap-cout'],
              ['Loyer mensuel', `${devis.loyerMensuel.toLocaleString('fr-FR')} € + ${devis.chargesMensuelles.toLocaleString('fr-FR')} € charges`, 'recap-loyer'],
              ['Trafic estimé', `≈ ${devis.traficEstime} clients/j`, 'recap-trafic'],
              ['CA potentiel', `≈ ${devis.caPotentielJour.toLocaleString('fr-FR')} €/j`, 'recap-ca'],
              ['Point mort estimé', devis.pointMortJours ? `≈ ${devis.pointMortJours} jours` : 'non atteignable en l\'état', 'recap-point-mort'],
              ['Surface', `${devis.surface} m²`, 'recap-surface'],
            ].map(([l, v, tid]) => (
              <div key={tid} className="rounded-lg border border-ink-600 bg-ink-900/40 p-3" data-testid={tid}>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p>
                <p className="text-sm font-bold text-foreground">{v}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            <input value={nomMagasin} onChange={e => setNomMagasin(e.target.value)}
              placeholder={`Nom du magasin (ex : ${getTypeMagasin(typeId)?.nom} ${getLocalisation(localisationId)?.nom})`}
              className="flex-1 rounded-lg border border-ink-600 bg-ink-900/60 px-3 py-2 text-sm text-foreground"
              data-testid="nom-magasin-input" />
            <div className="flex gap-2">
              {(mode === 'premier' ? [['emprunt', 'Emprunt bancaire']] : [['tresorerie', 'Trésorerie'], ['emprunt', 'Emprunt bancaire']]).map(([id, l]) => (
                <button key={id} onClick={() => setFinancement(id)}
                  className={`rounded-lg border px-3 py-2 text-xs font-bold ${financement === id ? 'border-accent text-accent bg-accent/10' : 'border-ink-600 text-slate-400'}`}
                  data-testid={`financement-${id}`}>{l}</button>
              ))}
            </div>
          </div>
          {mode === 'premier' && (
            <p className="text-xs text-sky-300" data-testid="financement-impose">
              🏦 Le lancement de la société est obligatoirement financé par emprunt bancaire ({devis.coutOuverture.toLocaleString('fr-FR')} € sur 48 mois) — votre trésorerie de départ reste disponible pour l'exploitation.
            </p>
          )}
          {mode !== 'premier' && !fondsOk && <p className="text-xs text-red-300" data-testid="fonds-insuffisants">Trésorerie insuffisante ({Math.round(treasury).toLocaleString('fr-FR')} €) — choisissez le financement par emprunt.</p>}
          {mode !== 'premier' && financement === 'emprunt' && <p className="text-xs text-sky-300">Emprunt de {devis.coutOuverture.toLocaleString('fr-FR')} € sur 48 mois (capital + intérêts) via votre banque.</p>}
        </div>
      )}

      <div className="flex justify-between pt-2 border-t border-ink-700">
        <button onClick={() => setEtape(Math.max(minEtape, etape - 1))} disabled={etape === minEtape}
          className="flex items-center gap-1 rounded-lg border border-ink-600 px-4 py-2 text-sm text-slate-300 disabled:opacity-40"
          data-testid="tunnel-precedent"><ChevronLeft className="w-4 h-4" /> Précédent</button>
        {etape < 4 ? (
          <button onClick={() => peutContinuer && setEtape(etape + 1)} disabled={!peutContinuer}
            className="flex items-center gap-1 rounded-lg bg-accent hover:bg-accent-hover text-white px-4 py-2 text-sm font-bold disabled:opacity-40"
            data-testid="tunnel-suivant">Suivant <ChevronRight className="w-4 h-4" /></button>
        ) : (
          <button onClick={confirmer} disabled={!fondsOk}
            className="rounded-lg bg-accent hover:bg-accent-hover text-white px-5 py-2 text-sm font-bold disabled:opacity-40"
            data-testid="tunnel-confirmer">Confirmer l'ouverture</button>
        )}
      </div>
    </div>
  );
}
